import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../../../contexts/ToastContext";
import { useUser } from "../../../../../contexts/UserContext/UserContext";
import useInsuranceSaleSendPaymentLink from "../../../hooks/useInsuranceSaleSendPaymentLink";
import {
    buildInsuranceSalesReturnUrl,
    createInsuranceSaleCheckout,
    getCompanyId,
} from "../../../lib/insuranceSaleApi";
import InsuranceSalePaymentFields from "./InsuranceSalePaymentFields";
import { isSolaGateway } from "../../../../../lib/sola/solaPaymentApi";
import useSolaCardPayment, { isSolaPaymentCancelled } from "../../../../../components/payment/useSolaCardPayment";

const buildReturnUrl = (reservationId, payment, process) =>
    buildInsuranceSalesReturnUrl(reservationId, payment, process);

const PaySaleStep = ({
    reservationId,
    amount,
    customerEmail,
    reservationProcess,
    extensionDropoffDate,
    onSuccess,
}) => {
    const { t } = useTranslation("sellInsurance");
    const toast = useToast();
    const { myCompanyData } = useUser();
    const [loading, setLoading] = useState(false);
    const { sendPaymentLink, loading: sendLinkLoading, bookingAgentConfigured } =
        useInsuranceSaleSendPaymentLink();

    const amountDue = Number(amount || 0);
    const { requestSolaCardPayment, solaCardModal } = useSolaCardPayment();

    const handleStripe = async () => {
        if (loading || sendLinkLoading) {
            return;
        }

        setLoading(true);
        try {
            const companyId = getCompanyId(myCompanyData);

            // Sola charges on the spot; the backend starts or extends the coverage itself. Ending
            // on Stripe's return URL gives the same success / cancelled notice either way.
            if (isSolaGateway(myCompanyData)) {
                try {
                    await requestSolaCardPayment({
                        reservationId,
                        reservationProcess,
                        amount: amountDue,
                        customerEmail,
                        extensionDropoffDate,
                        companyId,
                    });
                } catch (solaError) {
                    if (!isSolaPaymentCancelled(solaError)) throw solaError;
                    window.location.href = buildReturnUrl(reservationId, "cancel", reservationProcess);
                    return;
                }
                window.location.href = buildReturnUrl(reservationId, "success", reservationProcess);
                return;
            }

            const response = await createInsuranceSaleCheckout({
                reservationId,
                amount: amountDue,
                customerEmail,
                companyId,
                reservationProcess,
                extensionDropoffDate,
                successUrl: buildReturnUrl(reservationId, "success", reservationProcess),
                cancelUrl: buildReturnUrl(reservationId, "cancel", reservationProcess),
            });

            if (response?.success === false) {
                throw new Error(response?.message || t("pay.stripeError"));
            }

            const paymentUrl = response?.data?.paymentUrl;
            if (!paymentUrl) {
                throw new Error(t("pay.missingPaymentUrl"));
            }

            window.location.href = paymentUrl;
        } catch (error) {
            toast.error({
                message: t("pay.stripeError"),
                description: error?.response?.data?.message || error?.message,
            });
            setLoading(false);
        }
    };

    const handleSendPaymentLink = () =>
        sendPaymentLink({
            reservationId,
            amount: amountDue,
            customerEmail,
            reservationProcess,
            extensionDropoffDate,
            onSuccess,
        });

    return (
        <>
            {solaCardModal}
            <InsuranceSalePaymentFields
            amountDue={amountDue}
            showStripeButton
            stripeLoading={loading}
            onStripePay={handleStripe}
            showSendLinkButton
            sendLinkLoading={sendLinkLoading}
            sendLinkDisabled={!bookingAgentConfigured}
            sendLinkDisabledReason={bookingAgentConfigured ? undefined : t("pay.bookingAgentRequired")}
            onSendLink={handleSendPaymentLink}
        />
        </>
    );
};

PaySaleStep.propTypes = {
    reservationId: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    customerEmail: PropTypes.string,
    reservationProcess: PropTypes.oneOf(["insuranceSale", "insuranceSaleExtension"]).isRequired,
    extensionDropoffDate: PropTypes.string,
    onSuccess: PropTypes.func,
};

PaySaleStep.defaultProps = {
    customerEmail: "",
    extensionDropoffDate: undefined,
    onSuccess: undefined,
};

export default PaySaleStep;
